import {
  dashboard20240730Client,
  type DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  SequenceMenu,
  Skeleton,
  Button,
  GreyGrey60,
  MagicTableCell,
  MagicTableCellQuoteStatus,
  MagicTable,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  SelectAllPrompt,
  ActiveFilter,
  IconSignature,
  IconPlus,
  Box,
  Flex,
  Link
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import { QuoteView } from 'modules/Quotes/types'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { useNavigate } from 'react-router-dom'
import {
  QuoteTemplatesList,
  QUOTE_TEMPLATES_LIST_HEIGHT
} from './quoteTemplates/QuoteTemplatesList'
import { CreateTemplateFromQuoteMenuItem } from 'modules/common/menuItems/quotes/CreateTemplateFromQuoteMenuItem'
import { AMENDMENT_QUOTE_UNAVAILABLE_REASON } from 'modules/common/menuItems/quotes/quotes.constants'
import { DuplicateQuoteMenuItem } from 'modules/common/menuItems/quotes/DuplicateQuoteMenuItem'
import { ArchiveQuoteMenuItem } from 'modules/common/menuItems/quotes/ArchiveQuoteMenuItem'
import { DownloadQuotePdfMenuItem } from 'modules/common/menuItems/quotes/DownloadQuotePdfMenuItem'
import { FC, useCallback, useState } from 'react'
import { useQuotesMagicTable } from './QuotesMagicTable/useQuotesMagicTable'
import { QuotesSegmentedFilters } from './QuotesSegmentedFilters'
import { useQuery, useSequenceInfiniteQuery } from '@sequencehq/api'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { QuoteBulkActions } from './QuoteBulkActions'
import { BulkActionsInFlight } from 'modules/BulkActions'
import { quoteBulkActionConfig } from './quoteBulkAction.config'

type Props = {
  onSaveFilters?: (activeFilters: ActiveFilter[], sortBy: string | null) => void
  onUpdateSavedFilters?: (
    updatedActiveFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
  forcedActiveFilters?: ActiveFilter[]
  lockedControls?: boolean
  onFiltersApiQueryParamsChanged?: (params: string) => void
  showSegmentedFilters?: boolean
}

const QuotesMagicTable: FC<Props> = ({
  onSaveFilters,
  onUpdateSavedFilters,
  forcedActiveFilters = [],
  lockedControls = false,
  onFiltersApiQueryParamsChanged,
  showSegmentedFilters = false
}) => {
  const navigate = useNavigate()
  const { can } = usePermissions()

  const [disableBulkActionCheckboxes, setDisableBulkActionCheckboxes] =
    useState<boolean>(false)
  const [showBulkActionsInFlight, setShowBulkActionsInFlight] = useState(false)

  // Use the custom hook for table logic
  const {
    filters,
    activeFilters,
    sortBy,
    onChangeActiveFilters,
    onChangeSortBy,
    infiniteQuery,
    selectedQuotes,
    setSelectedQuotes,
    selectAll
  } = useQuotesMagicTable({
    forcedActiveFilters,
    onFiltersApiQueryParamsChanged
  })

  const handleChangeActiveBulkActionCount = useCallback(
    (bulkActionCount: number) => {
      setDisableBulkActionCheckboxes(bulkActionCount > 0)
    },
    []
  )

  const handleChangeHasBulkActions = useCallback((hasBulkActions: boolean) => {
    setShowBulkActionsInFlight(hasBulkActions)
  }, [])

  // Archive is the only bulk action; gate on the same permission as the
  // single-quote archive plus the shared bulk-action permission.
  const canUseQuoteBulkActions =
    can('org.bulkAction:create') && can('org.quote:archive')

  const canManageQuotes = can(['org.quote:create', 'org.quote:update'])

  // Matches QuoteTemplatesList's query/args to reuse the cache entry.
  const templatesInfiniteQuery = useSequenceInfiniteQuery(
    dashboard20240730Client.getListQuoteTemplates,
    { limit: 100 },
    { enabled: canManageQuotes }
  )
  const hasQuoteTemplates =
    (templatesInfiniteQuery.infiniteQuery.data?.pages[0]?.items.length ?? 0) > 0

  // Create wrapper functions for onSaveFilters and onUpdateSavedFilters
  const handleSaveFilters = useCallback(() => {
    if (onSaveFilters) {
      onSaveFilters(activeFilters, sortBy)
    }
  }, [onSaveFilters, activeFilters, sortBy])

  // Only call onUpdateSavedFilters when explicitly triggered by user actions
  // This prevents the constant save requests when viewing a saved view
  const handleUpdateSavedFilters = useCallback(() => {
    if (onUpdateSavedFilters) {
      onUpdateSavedFilters(activeFilters, sortBy)
    }
  }, [onUpdateSavedFilters, activeFilters, sortBy])

  type ListQuoteModel = DashboardApi20240730.GetQuotes.Quote

  const additionalToolbarComponentsV3 = showSegmentedFilters
    ? {
        additionalToolbarComponentsV3: {
          start: <QuotesSegmentedFilters />
        }
      }
    : {}

  return (
    <Box height="100%" width="100%" data-testid="quotes.magicTable">
      <CurrentUserId>
        {userId => (
          <>
            <MagicTable<QuoteView>
              activeFilters={activeFilters}
              onChangeActiveFilters={onChangeActiveFilters}
              sortBy={sortBy}
              onChangeSortBy={onChangeSortBy}
              entityNamePlural="quotes"
              sequenceUserId={userId}
              infiniteQuery={infiniteQuery}
              additionalTableHeaderRow={({ visibleTableWidth }) => {
                if (!canUseQuoteBulkActions || !selectAll.showPrompt) {
                  return null
                }

                return (
                  <SelectAllPrompt
                    entityName="quotes"
                    onSelectAll={selectAll.onSelectAll}
                    showClearSelection={selectAll.showClearSelection}
                    onClearSelection={selectAll.onClearSelection}
                    visibleTableWidth={visibleTableWidth}
                    loading={selectAll.loading}
                    total={selectAll.total}
                    totalSelected={selectAll.totalSelected}
                  />
                )
              }}
              disableCheckboxes={
                !canUseQuoteBulkActions ||
                disableBulkActionCheckboxes ||
                selectAll.loading
              }
              bulkActions={
                canUseQuoteBulkActions
                  ? {
                      selectedItems: selectedQuotes,
                      onSelectedItemsChange: setSelectedQuotes,
                      bar: (
                        selectedRows: QuoteView['model'][],
                        clearSelectedRows: () => void
                      ) => {
                        if (selectAll.loading) {
                          return undefined
                        }

                        const bulkActionBar = (
                          <QuoteBulkActions
                            selectedRows={selectedRows}
                            clearSelectedRows={clearSelectedRows}
                            popoverOffset={[0, 0]}
                          >
                            <Box
                              position="absolute"
                              top="50%"
                              left="50%"
                              transform="translate(-50%, -50%)"
                            />
                          </QuoteBulkActions>
                        )

                        if (!showBulkActionsInFlight) {
                          return bulkActionBar
                        }

                        return null
                      }
                    }
                  : undefined
              }
              rowPath={quote =>
                can('org.quote:update')
                  ? `/quotes/${quote.id}`
                  : `/quotes/${quote.id}/preview`
              }
              scrollAreaHeader={
                canManageQuotes && hasQuoteTemplates
                  ? {
                      content: <QuoteTemplatesList />,
                      height: QUOTE_TEMPLATES_LIST_HEIGHT
                    }
                  : undefined
              }
              onSaveFilters={onSaveFilters ? handleSaveFilters : undefined}
              onUpdateSavedFilters={
                onUpdateSavedFilters ? handleUpdateSavedFilters : undefined
              }
              lockedControls={lockedControls}
              {...additionalToolbarComponentsV3}
              columns={[
                {
                  id: 'title',
                  accessorFn: row => ({
                    title: row.title,
                    archivedAt: row.archivedAt
                  }),
                  header: 'Title',
                  cell: value => {
                    const { archivedAt, title } = value.getValue<{
                      archivedAt: ListQuoteModel['archivedAt']
                      title: ListQuoteModel['title']
                    }>()
                    return (
                      <MagicTableCell
                        textColor={archivedAt && GreyGrey60}
                        text={`${title || 'Untitled'}${
                          archivedAt ? ' (archived)' : ''
                        }`}
                      />
                    )
                  }
                },
                {
                  id: 'quoteNumber',
                  accessorFn: row => ({
                    archivedAt: row.archivedAt,
                    quoteNumber: row.quoteNumber
                  }),
                  header: 'Number',
                  cell: value => {
                    const { archivedAt, quoteNumber } = value.getValue<{
                      archivedAt: ListQuoteModel['archivedAt']
                      quoteNumber: ListQuoteModel['quoteNumber']
                    }>()
                    return (
                      <MagicTableCell
                        textColor={archivedAt && GreyGrey60}
                        text={quoteNumber}
                      />
                    )
                  }
                },
                {
                  id: 'customer',
                  accessorKey: 'customerId',
                  header: 'Customer',
                  cell: value => {
                    const customerId =
                      value.getValue<ListQuoteModel['customerId']>()
                    return customerId ? (
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        height="40px"
                        pl="6px"
                      >
                        <CustomerPreviewCardPill
                          customerId={customerId}
                          variant="ghost"
                        />
                      </Flex>
                    ) : (
                      <MagicTableCell text="" />
                    )
                  }
                },
                {
                  id: 'status',
                  header: 'Status',
                  accessorFn: row => ({
                    archivedAt: row.archivedAt,
                    publishedAt: row.publishedAt,
                    isExpired: row.isExpired,
                    status: row.status,
                    approvalWorkflowId: row.approvalWorkflowId
                  }),
                  cell: value => {
                    const {
                      archivedAt,
                      status,
                      publishedAt,
                      isExpired,
                      approvalWorkflowId
                    } =
                      value.getValue<
                        Pick<
                          ListQuoteModel,
                          | 'archivedAt'
                          | 'status'
                          | 'publishedAt'
                          | 'isExpired'
                          | 'approvalWorkflowId'
                        >
                      >()

                    return (
                      <MagicTableCellQuoteStatus
                        status={status}
                        isArchived={Boolean(archivedAt)}
                        isPublished={Boolean(publishedAt)}
                        isExpired={Boolean(isExpired)}
                        hasApprovalWorkflow={Boolean(approvalWorkflowId)}
                      />
                    )
                  }
                },
                {
                  id: 'user',
                  accessorFn: row => ({
                    archivedAt: row.archivedAt,
                    createdBy: row.createdBy
                  }),
                  header: 'Owner',
                  cell: value => {
                    const { archivedAt, createdBy } = value.getValue<{
                      archivedAt: ListQuoteModel['archivedAt']
                      createdBy: ListQuoteModel['createdBy']
                    }>()

                    const userQuery = useQuery(
                      dashboard20240730Client.getUser,
                      {
                        id: createdBy
                      }
                    )

                    if (userQuery.isPending) {
                      return <Skeleton width="100%" height="100%" />
                    }

                    return (
                      <MagicTableCell
                        textColor={archivedAt && GreyGrey60}
                        text={userQuery.data?.email || ''}
                      />
                    )
                  }
                },
                {
                  id: 'expiresAt',
                  accessorFn: row => ({
                    expiresAt: row.expiresAt,
                    expiryDays: row.expiryDays,
                    archivedAt: row.archivedAt
                  }),
                  header: 'Expires',
                  cell: value => {
                    const { expiresAt, expiryDays, archivedAt } =
                      value.getValue<{
                        expiresAt: ListQuoteModel['expiresAt']
                        expiryDays: ListQuoteModel['expiryDays']
                        archivedAt: ListQuoteModel['archivedAt']
                      }>()
                    return (
                      <MagicTableCell
                        textColor={archivedAt && GreyGrey60}
                        text={
                          expiresAt
                            ? dateTimeWithFormat(expiresAt, 'd MMM yyyy')
                            : expiryDays
                              ? `${expiryDays} days after publishing`
                              : ''
                        }
                      />
                    )
                  }
                },
                {
                  id: 'acceptedAt',
                  accessorFn: row => ({
                    acceptedAt: row.acceptedAt,
                    archivedAt: row.archivedAt
                  }),
                  header: 'Signature date',
                  cell: value => {
                    const { acceptedAt, archivedAt } = value.getValue<{
                      acceptedAt: ListQuoteModel['acceptedAt']
                      archivedAt: ListQuoteModel['archivedAt']
                    }>()
                    return (
                      <MagicTableCell
                        textColor={archivedAt && GreyGrey60}
                        text={
                          acceptedAt
                            ? dateTimeWithFormat(acceptedAt, 'd MMM yyyy')
                            : ''
                        }
                      />
                    )
                  },
                  enableSorting: true
                }
              ]}
              kebabMenu={(model: ListQuoteModel, props) => {
                const isPublished = Boolean(model.publishedAt)
                const isAmendment = model.dealType === 'AMENDMENT'

                // If none of these are true
                // there aren't really any actions to use
                // so we don't show the kebab menu at all!
                const showKebabMenu =
                  isPublished ||
                  can('org.quote:create') ||
                  can('org.quote:archive')

                if (!showKebabMenu) {
                  return null
                }

                return (
                  <SequenceMenu
                    data-testid={`quotes.${model.id}.menu`}
                    buttonSize="small"
                    alignment="bottom-right"
                    items={[
                      <DuplicateQuoteMenuItem
                        key="duplicate-quote"
                        quoteId={model.id}
                        disabled={isAmendment}
                        disabledReason={
                          isAmendment
                            ? AMENDMENT_QUOTE_UNAVAILABLE_REASON
                            : undefined
                        }
                        onSuccess={(newQuoteId: string) =>
                          navigate(`/quotes/${newQuoteId}`)
                        }
                      />,
                      <CreateTemplateFromQuoteMenuItem
                        key="create-template-from-quote"
                        quoteId={model.id}
                        disabled={isAmendment}
                        disabledReason={
                          isAmendment
                            ? AMENDMENT_QUOTE_UNAVAILABLE_REASON
                            : undefined
                        }
                      />,
                      <DownloadQuotePdfMenuItem
                        key="download-pdf"
                        quoteId={model.id}
                        quoteNumber={model.quoteNumber}
                        isPublished={isPublished}
                      />,
                      ({ setIsOpen }) =>
                        !model.archivedAt && (
                          <ArchiveQuoteMenuItem
                            key="archive-quote"
                            data-testid={`quotes.${model.id}.archive`}
                            quoteId={model.id}
                            onSuccess={() => {
                              setIsOpen?.(false)
                            }}
                          />
                        )
                    ]}
                    {...props}
                  />
                )
              }}
              emptyStateUnfiltered={entityNamePlural => (
                <MagicTableEmptyState
                  data-testid="quotes.emptyState"
                  entityNamePlural={entityNamePlural}
                  imageSrc="/img/empty/quotes.png"
                  learnMoreText="Create order forms, proposals and sales quotes with custom deal terms in minutes"
                  learnMoreHref="https://docs.sequencehq.com/cpq/create-quotes"
                  buttonNew={
                    <ProtectedButton permission="org.quote:create">
                      <Button
                        as={Link.Unstyled}
                        to="/quotes/new"
                        variant="primary"
                        leadingIcon={<IconPlus color="inherit" />}
                      >
                        New quote
                      </Button>
                    </ProtectedButton>
                  }
                />
              )}
              emptyStateFiltered={(entityNamePlural, clearFilters) => (
                <MagicTableFilterEmptyState
                  entityNamePlural={entityNamePlural}
                  entityIcon={IconSignature}
                  actionClearFilters={clearFilters}
                  actionViewAll={() => navigate('/quotes')}
                  variant="TABLE"
                />
              )}
              filters={filters}
            />
          </>
        )}
      </CurrentUserId>
      <BulkActionsInFlight
        selectionCount={selectedQuotes.length}
        onChangeActiveBulkActionCount={handleChangeActiveBulkActionCount}
        onChangeHasBulkActions={handleChangeHasBulkActions}
        config={quoteBulkActionConfig}
      />
    </Box>
  )
}

export default QuotesMagicTable
